/**
 * Locations List Item Component
 *
 * Individual location item in the scrollable list.
 * Shows location details, available dates, and inventory status.
 * Uses Tailwind CSS for styling (light DOM rendering).
 *
 * @element peach-locations-list-item
 *
 * @fires location-selected - Emitted when card is clicked
 *
 * @example
 * ```html
 * <peach-locations-list-item
 *   .location=${location}
 *   .selected=${false}>
 * </peach-locations-list-item>
 * ```
 */

import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { locationStore } from "@/features/locations/stores/location-store";
import type { Location, Stop } from "@/types/location";
import { cn } from "@/utils/cn";
import "@/features/ui/card";

@customElement("peach-locations-list-item")
export class LocationsListItemComponent extends LitElement {
  /**
   * Maximum number of date badges to display before showing "+X more"
   */
  private static readonly MAX_VISIBLE_DATES = 3;

  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Location data
   */
  @property({ type: Object })
  declare location: Location;

  /**
   * Whether this location is selected
   */
  @property({ type: Boolean })
  declare selected: boolean;

  constructor() {
    super();
    this.selected = false;
  }

  /**
   * Handle card click - select this location
   */
  private handleClick(e: Event) {
    e.preventDefault();
    e.stopPropagation();

    console.log("[LocationsListItem] Clicked location:", {
      id: this.location.id,
      name: this.location.name,
      currentSelectedId: locationStore.getState().selectedLocationId,
    });

    locationStore.getState().selectLocation(this.location.id);

    console.log("[LocationsListItem] After selectLocation:", {
      newSelectedId: locationStore.getState().selectedLocationId,
    });

    this.dispatchEvent(
      new CustomEvent("location-selected", {
        detail: { location: this.location },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Format date string for display
   */
  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }

  /**
   * Render available dates section - only future dates, deduplicated
   */
  private renderAvailableDates(stops: Stop[]) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filter to future dates and deduplicate by date
    const seenDates = new Set<string>();
    const futureStops = stops.filter((stop) => {
      if (new Date(stop.date) < today) return false;
      if (seenDates.has(stop.date)) return false;
      seenDates.add(stop.date);
      return true;
    });

    if (futureStops.length === 0) {
      return html`
        <p class="text-xs text-subtle">No upcoming dates</p>
      `;
    }

    const maxVisible = LocationsListItemComponent.MAX_VISIBLE_DATES;

    return html`
      <div class="flex flex-wrap items-center gap-1">
        <peach-icon name="calendar" size="sm" class="text-brand"></peach-icon>
        ${futureStops.slice(0, maxVisible).map((stop) => {
          return html`
            <span class="px-2 py-0.5 rounded text-xs font-medium bg-brand-accent text-white">
              ${this.formatDate(stop.date)}
            </span>
          `;
        })}
        ${
          futureStops.length > maxVisible
            ? html`
              <span class="text-xs text-muted">
                +${futureStops.length - maxVisible} more
              </span>
            `
            : ""
        }
      </div>
    `;
  }

  render() {
    const { name, address, stops, distance } = this.location;

    return html`
      <peach-card
        variant="interactive"
        .selected=${this.selected}
        role="listitem"
        aria-selected=${this.selected}
        @click=${this.handleClick}
      >
        <!-- Header: Location Name & Distance -->
        <div class="flex items-baseline justify-between">
          <h3 class=${cn("font-semibold flex-1")}>
            ${name}
          </h3>
          ${
            distance !== undefined
              ? html`
                <span class="text-sm text-subtle font-medium">
                  ${distance.toFixed(1)} mi
                </span>
              `
              : ""
          }
        </div>

        <!-- Address -->

        <p class="text-sm mb-1">
          <peach-icon name="map-pin" size="sm" class="text-brand"></peach-icon>
          ${address}
        </p>

        <!-- Available Dates -->
        ${this.renderAvailableDates(stops)}
      </peach-card>
    `;
  }
}

/**
 * TypeScript declaration for JSX/Liquid integration
 */
declare global {
  interface HTMLElementTagNameMap {
    "peach-locations-list-item": LocationsListItemComponent;
  }
}
